import { useEffect, useState } from 'react';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { Link } from 'react-router-dom';
import { CircleDot, Download, Plus, Repeat, Search, Tag, Upload } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useSubscriptions,
  useSubscriptionCategories,
  useCreateSubscription,
  useBulkDeleteSubscriptions,
} from '../hooks/useSubscriptions';
import { usePermissions } from '../hooks/usePermissions';
import { useI18n } from '../contexts/I18nContext';
import { downloadDirectoryExcel } from '../lib/directoryExcelApi';
import { ExcelImportDialog } from '../components/directory/ExcelImportDialog';
import { BulkDeleteBar } from '../components/directory/BulkDeleteBar';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { useToast } from '../components/tasks/linear/Toast';
import {
  DEFAULT_SUBSCRIPTION_CATEGORIES,
  SUBSCRIPTION_FREQUENCY_LABELS,
  subscriptionCategoryLabel,
  type SubscriptionFilters,
  type SubscriptionInput,
  type SubscriptionFrequency,
  type SubscriptionSortField,
} from '../lib/subscriptionsApi';
import { FadeIn, SkeletonTable } from '../components/ui/Skeleton';
import { Select } from '../components/ui/Select';
import { MobileFilterSheet, type FilterDescriptor } from '../components/ui/MobileFilterSheet';
import { SortHeader, sortHeaderProps } from '../components/ui/SortHeader';
import { SubscriptionFormModal } from '../components/subscriptions/SubscriptionFormModal';

// Delete is a soft delete (isActive=false) — default to active-only so deleted
// rows actually leave the list (same fix as suppliers/clients).
const DEFAULT_FILTERS: SubscriptionFilters = { sort: 'name', page: 1, limit: 20, active: 'true' };

export function SubscriptionsListPage() {
  const [filters, setFilters] = useState<SubscriptionFilters>(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Bulk selection (rows on the current page only; cleared on filter/page change).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isLoading, isError } = useSubscriptions(filters);
  const subscriptions = data?.data ?? [];
  const meta = data?.meta;

  // Settings-managed nomenclature; seed defaults as fallback while loading.
  const { data: categoryDefs } = useSubscriptionCategories();
  const categories =
    categoryDefs && categoryDefs.length > 0 ? categoryDefs : DEFAULT_SUBSCRIPTION_CATEGORIES;

  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useI18n();
  const createMutation = useCreateSubscription();
  const bulkDeleteMutation = useBulkDeleteSubscriptions();
  const { can } = usePermissions();
  const canCreate = can('subscriptions', 'canCreate');
  const canView = can('subscriptions', 'canView');
  const canDelete = can('subscriptions', 'canDelete');

  async function handleExport() {
    setExporting(true);
    try {
      await downloadDirectoryExcel('subscriptions');
    } catch {
      toast.show(t('subscriptions.exportError'));
    } finally {
      setExporting(false);
    }
  }

  function patchFilters(patch: Partial<SubscriptionFilters>) {
    setSelected(new Set());
    setFilters((prev) => ({ ...prev, ...patch, page: 1 }));
  }

  // Selection helpers: only rows visible on the current page can be selected.
  const allVisibleSelected =
    subscriptions.length > 0 && subscriptions.every((s) => selected.has(s.id));

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllVisible() {
    setSelected(allVisibleSelected ? new Set() : new Set(subscriptions.map((s) => s.id)));
  }

  function goToPage(page: number) {
    setSelected(new Set());
    setFilters((p) => ({ ...p, page }));
  }

  function confirmBulkDelete() {
    bulkDeleteMutation.mutate([...selected], {
      onSuccess: () => {
        setConfirmOpen(false);
        setSelected(new Set());
      },
    });
  }

  // Header sort: first click ascending, second click on the same column flips
  // to descending. Selection is cleared because the page content changes.
  function header(field: SubscriptionSortField) {
    return sortHeaderProps(filters.sort ?? 'name', field, (token) => {
      setSelected(new Set());
      setFilters((prev) => ({ ...prev, sort: token, page: 1 }));
    });
  }

  // Search as the user types (debounced); Enter still searches right away.
  const debouncedSearch = useDebouncedValue(searchInput.trim());
  useEffect(() => {
    if (debouncedSearch !== (filters.search ?? '')) {
      patchFilters({ search: debouncedSearch || undefined });
    }
    // Only a settled keystroke triggers this, not every filter change.
  }, [debouncedSearch]);

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    patchFilters({ search: searchInput.trim() || undefined });
  }

  async function handleCreate(input: SubscriptionInput) {
    await createMutation.mutateAsync(input);
    setShowCreate(false);
  }

  const mobileFilters: FilterDescriptor[] = [
    {
      kind: 'select',
      id: 'category',
      label: t('common.category'),
      icon: <Tag size={16} />,
      value: filters.category ?? '',
      onChange: (v) => patchFilters({ category: v || undefined }),
      options: categories.map((c) => ({ value: c.code, label: c.label })),
      placeholder: t('subscriptions.filter.allCategories'),
    },
    {
      kind: 'select',
      id: 'active',
      label: t('common.status'),
      icon: <CircleDot size={16} />,
      value: filters.active ?? '',
      onChange: (v) => patchFilters({ active: (v || undefined) as SubscriptionFilters['active'] }),
      options: [
        { value: 'true', label: t('common.active') },
        { value: 'false', label: t('common.inactive') },
      ],
      placeholder: t('subscriptions.filter.anyStatus'),
    },
  ];

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-2">
          <Repeat className="text-brand-600" size={24} />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('subscriptions.title')}</h1>
            <p className="text-sm text-gray-500">{t('subscriptions.subtitle')}</p>
          </div>
        </div>
        {/* Below lg the actions are a grid of equal halves (the primary one
            spans the row when the count is odd), not a ragged wrap. */}
        <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-wrap lg:items-center [&>*:last-child:nth-child(odd)]:col-span-2">
          {canView && (
            <button
              type="button"
              onClick={() => void handleExport()}
              disabled={exporting}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Download size={16} />
              {exporting ? t('common.exporting') : t('common.exportExcel')}
            </button>
          )}
          {canCreate && (
            <button
              type="button"
              onClick={() => setShowImport(true)}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Upload size={16} />
              {t('common.importExcel')}
            </button>
          )}
          {canCreate && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              <Plus size={16} />
              {t('subscriptions.create')}
            </button>
          )}
        </div>
      </div>

      {/* Below lg (phones, and tablets where the sidebar eats half the
          width): search + one Filtre button (bottom sheet), like Leaduri,
          instead of the stacked fields. */}
      <div className="mt-4 flex items-center gap-2 lg:hidden">
        <form onSubmit={submitSearch} className="relative min-w-0 flex-1">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('subscriptions.search')}
            aria-label={t('common.search')}
            className="ui-input min-h-[44px] pl-9"
          />
        </form>
        <MobileFilterSheet
          filters={mobileFilters}
          onClearAll={() => patchFilters({ category: undefined, active: undefined })}
        />
      </div>

      {/* Filters */}
      <div className="mt-6 hidden gap-3 lg:flex lg:items-end">
        <form onSubmit={submitSearch} className="flex-1">
          <label className="ui-field-label">{t('common.search')}</label>
          <div className="relative">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('subscriptions.search')}
              className="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
        </form>
        <Select
          label={t('common.category')}
          clearable
          value={filters.category ?? ''}
          onChange={(v) => patchFilters({ category: v || undefined })}
          placeholder={t('subscriptions.filter.allCategories')}
          options={categories.map((c) => ({ value: c.code, label: c.label }))}
          className="w-full sm:w-44"
        />
        <Select
          label={t('common.status')}
          clearable
          value={filters.active ?? ''}
          onChange={(v) =>
            patchFilters({ active: (v || undefined) as SubscriptionFilters['active'] })
          }
          placeholder={t('subscriptions.filter.anyStatus')}
          options={[
            { value: 'true', label: t('common.active') },
            { value: 'false', label: t('common.inactive') },
          ]}
          className="w-full sm:w-40"
        />
      </div>

      {/* Table */}
      <div className="mt-6">
        {isLoading && <SkeletonTable rows={8} columns={8} />}
        {isError && <p className="text-red-600">{t('subscriptions.loadError')}</p>}
        {!isLoading && !isError && subscriptions.length === 0 && (
          <p className="py-12 text-center text-gray-500">{t('subscriptions.empty')}</p>
        )}
        {!isLoading && !isError && subscriptions.length > 0 && (
          <FadeIn>
            {/* Below lg: one card per row instead of a table that only
                scrolled sideways; the name leads, the details follow. */}
            <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white lg:hidden">
              {subscriptions.map((s) => (
                <li key={s.id} className="flex items-start gap-3 px-3 py-3">
                  {canDelete && (
                    <input
                      type="checkbox"
                      checked={selected.has(s.id)}
                      onChange={() => toggleRow(s.id)}
                      aria-label={s.name}
                      className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <Link
                        to={`/subscriptions/${s.id}`}
                        className="min-w-0 truncate font-medium text-gray-900 hover:text-brand-700 hover:underline"
                      >
                        {s.name}
                      </Link>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                          s.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {s.isActive ? t('common.active') : t('common.inactive')}
                      </span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-gray-500">
                      {[subscriptionCategoryLabel(s.category, categories), s.supplier?.name]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                    <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <dt className="text-gray-500">{t('subscriptions.amount')}</dt>
                        <dd className="tabular-nums text-gray-900">
                          {s.expectedAmount != null
                            ? `${s.expectedAmount.toLocaleString()} ${s.expectedCurrency ?? 'RON'}`
                            : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">{t('subscriptions.frequency')}</dt>
                        <dd className="text-gray-900">
                          {SUBSCRIPTION_FREQUENCY_LABELS[s.frequency as SubscriptionFrequency] ??
                            s.frequency}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">{t('subscriptions.paymentDay')}</dt>
                        <dd className="tabular-nums text-gray-900">
                          {s.frequency === 'monthly' ? (s.paymentDay ?? 1) : '—'}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </li>
              ))}
            </ul>
            <div className="hidden overflow-x-auto rounded-lg border border-gray-200 lg:block">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {canDelete && (
                      <th className="w-10 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleAllVisible}
                          aria-label={t('bulk.selected', { count: selected.size })}
                          className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                        />
                      </th>
                    )}
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      <SortHeader label={t('subscriptions.name')} {...header('name')} />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      <SortHeader label={t('subscriptions.category')} {...header('category')} />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      <SortHeader label={t('subscriptions.supplier')} {...header('supplier')} />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      <SortHeader label={t('subscriptions.amount')} {...header('expectedAmount')} />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      <SortHeader label={t('subscriptions.frequency')} {...header('frequency')} />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      <SortHeader label={t('subscriptions.paymentDay')} {...header('paymentDay')} />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      {t('subscriptions.patterns')}
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      <SortHeader label={t('common.status')} {...header('isActive')} />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {subscriptions.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      {canDelete && (
                        <td className="w-10 px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selected.has(s.id)}
                            onChange={() => toggleRow(s.id)}
                            aria-label={s.name}
                            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          />
                        </td>
                      )}
                      <td className="px-3 py-2 font-medium text-gray-900">
                        <Link
                          to={`/subscriptions/${s.id}`}
                          className="hover:text-brand-700 hover:underline"
                        >
                          {s.name}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-block rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
                          {subscriptionCategoryLabel(s.category, categories)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600">{s.supplier?.name ?? '—'}</td>
                      <td className="px-3 py-2 text-gray-600">
                        {s.expectedAmount != null
                          ? `${s.expectedAmount.toLocaleString()} ${s.expectedCurrency ?? 'RON'}`
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {SUBSCRIPTION_FREQUENCY_LABELS[s.frequency as SubscriptionFrequency] ??
                          s.frequency}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {s.frequency === 'monthly' ? (s.paymentDay ?? 1) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {s.merchantPatterns.map((p) => (
                            <span
                              key={p}
                              className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-xs font-mono text-gray-600"
                            >
                              {p}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                            s.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {s.isActive ? t('common.active') : t('common.inactive')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {meta && meta.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <span>
                  {t('subscriptions.pagination', {
                    page: meta.page,
                    totalPages: meta.totalPages,
                    total: meta.total,
                  })}
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={meta.page <= 1}
                    onClick={() => goToPage((filters.page ?? 1) - 1)}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    {t('common.previous')}
                  </button>
                  <button
                    disabled={meta.page >= meta.totalPages}
                    onClick={() => goToPage((filters.page ?? 1) + 1)}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            )}
          </FadeIn>
        )}
      </div>

      {canDelete && (
        <BulkDeleteBar
          count={selected.size}
          onDelete={() => setConfirmOpen(true)}
          onClear={() => setSelected(new Set())}
          error={bulkDeleteMutation.isError && !confirmOpen ? t('bulk.error') : undefined}
        />
      )}

      {confirmOpen && (
        <ConfirmDialog
          danger
          title={t('subscriptions.bulk.confirmTitle')}
          message={t('subscriptions.bulk.confirmMessage', { count: selected.size })}
          confirmLabel={t('bulk.delete')}
          busy={bulkDeleteMutation.isPending}
          error={bulkDeleteMutation.isError ? t('bulk.error') : undefined}
          onConfirm={confirmBulkDelete}
          onCancel={() => setConfirmOpen(false)}
        />
      )}

      {showCreate && (
        <SubscriptionFormModal
          title={t('subscriptions.create')}
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
          submitting={createMutation.isPending}
        />
      )}

      {showImport && (
        <ExcelImportDialog
          entityType="subscriptions"
          onClose={() => setShowImport(false)}
          onImportComplete={() =>
            void queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
          }
        />
      )}
    </div>
  );
}
